import React, { useState } from 'react';
import { useChat } from '../../context/ChatContext';
import { convsAPI, usersAPI } from '../../utils/api';
import { formatLastSeen, getAvatarColor, getInitials } from '../../utils/helpers';
import Avatar from '../UI/Avatar';

export default function ProfilePanel({ conv, onClose }) {
  const { loadConversations, conversations, openConversation } = useChat();
  const [confirm, setConfirm]   = useState('');
  const [showAddMembers, setShowAddMembers] = useState(false);
  const isGroup = conv?.type === 'group';
  const other   = conv?.other_user;

  if (!conv) return null;

  const handleLeave = async () => {
    if (confirm !== 'leave') { setConfirm('leave'); return; }
    await convsAPI.leave(conv.id);
    await loadConversations();
    onClose();
  };

  const handleMute = async () => {
    await convsAPI.mute(conv.id, conv.is_muted ? 0 : 1);
    await loadConversations();
  };

  // Participants: use from conv or fetch fresh
  const participants = conv.participants || [];
  const memberCount  = participants.length;

  return (
    <div className="profile-panel">
      <div className="profile-panel-header">
        <button className="icon-btn" onClick={onClose} style={{ color: 'white' }}>✕</button>
        <h2>{isGroup ? 'Group Info' : 'Contact Info'}</h2>
      </div>

      {/* Cover */}
      <div className="profile-cover">
        {isGroup
          ? <div className="avatar-placeholder-lg" style={{ background: getAvatarColor(conv.name) }}>{getInitials(conv.name)}</div>
          : (other?.avatar
              ? <img src={other.avatar} className="avatar-lg" alt={other.name} onError={e => { e.target.style.display='none'; }} />
              : <div className="avatar-placeholder-lg" style={{ background: getAvatarColor(other?.name) }}>{getInitials(other?.name)}</div>
            )
        }
        <h2>{isGroup ? conv.name : other?.name}</h2>
        <p>
          {isGroup
            ? `${memberCount} member${memberCount !== 1 ? 's' : ''}`
            : (other?.status === 'online' ? '🟢 Online' : formatLastSeen(other?.last_seen))
          }
        </p>
      </div>

      {/* About / Contact Info for direct chats */}
      {!isGroup && (
        <div className="profile-section">
          <h4>About</h4>
          <div className="profile-info-item">
            <span className="icon">ℹ️</span>
            <div>
              <div className="label">About</div>
              <div className="value">{other?.about || 'Hey there! I am using NChat.'}</div>
            </div>
          </div>
          <div className="profile-info-item">
            <span className="icon">📱</span>
            <div>
              <div className="label">Phone</div>
              <div className="value">{other?.phone || 'Not available'}</div>
            </div>
          </div>
          {other?.email && (
            <div className="profile-info-item">
              <span className="icon">✉️</span>
              <div>
                <div className="label">Email</div>
                <div className="value">{other.email}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Group description */}
      {isGroup && conv.description && (
        <div className="profile-section">
          <h4>Description</h4>
          <p style={{ fontSize: 14, color: '#111B21', lineHeight: 1.5 }}>{conv.description}</p>
        </div>
      )}

      {/* Group members */}
      {isGroup && (
        <div className="profile-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>{memberCount} Member{memberCount !== 1 ? 's' : ''}</h4>
            {conv.my_role === 'admin' && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowAddMembers(true)}
                style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                ➕ Add
              </button>
            )}
          </div>
          {participants.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
              <Avatar user={p} size={38} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                {p.status === 'online' && <div style={{ fontSize: 12, color: '#25D366' }}>Online</div>}
              </div>
              {p.role === 'admin' && (
                <span style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700, background: '#E8F5E9', padding: '2px 8px', borderRadius: 100 }}>Admin</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="profile-section">
        <h4>Actions</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button className="btn btn-ghost" style={{ justifyContent: 'flex-start' }} onClick={handleMute}>
            {conv.is_muted ? '🔔 Unmute notifications' : '🔇 Mute notifications'}
          </button>
          <button className="btn btn-ghost" style={{ justifyContent: 'flex-start' }}
            onClick={() => convsAPI.archive(conv.id, 1).then(loadConversations).then(onClose)}>
            📦 Archive chat
          </button>
          {isGroup && (
            <button className="btn btn-danger" style={{ justifyContent: 'flex-start', marginTop: 8 }} onClick={handleLeave}>
              {confirm === 'leave' ? '⚠️ Tap again to confirm' : '🚪 Leave group'}
            </button>
          )}
        </div>
      </div>

      {/* Add Members Modal */}
      {showAddMembers && (
        <AddMembersModal
          conv={conv}
          currentMembers={participants}
          onClose={() => setShowAddMembers(false)}
          onAdded={async () => {
            await loadConversations();
            setShowAddMembers(false);
          }}
        />
      )}
    </div>
  );
}

function AddMembersModal({ conv, currentMembers, onClose, onAdded }) {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [selected, setSelected] = useState([]);
  const [adding, setAdding]     = useState(false);

  React.useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const res = await usersAPI.search(query);
      if (res.data.success) {
        // Filter out existing members
        const existingIds = new Set(currentMembers.map(m => m.id));
        const filtered = res.data.data.users.filter(u => !existingIds.has(u.id));
        setResults(filtered);

        // Auto-select if there's an exact match on phone or name
        const exact = filtered.find(u => u.phone === query || u.name.toLowerCase() === query.toLowerCase());
        if (exact && !selected.find(s => s.id === exact.id)) {
          setSelected(prev => [...prev, exact]);
        }
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, currentMembers, selected]);

  const toggle = (u) => setSelected(s => s.find(x => x.id === u.id) ? s.filter(x => x.id !== u.id) : [...s, u]);

  const handleAdd = async () => {
    if (selected.length === 0) return;
    setAdding(true);
    await convsAPI.addMembers({ conversation_id: conv.id, members: selected.map(u => u.id) });
    await onAdded();
    setAdding(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>➕ Add Participants</h2>
          <button className="icon-btn dark" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {selected.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {selected.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#E8F5E9', padding: '3px 10px', borderRadius: 100, fontSize: 13 }}>
                  {u.name}
                  <button onClick={() => toggle(u)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#667781' }}>✕</button>
                </div>
              ))}
            </div>
          )}
          <input className="form-input" placeholder="Search people to add"
            value={query} onChange={e => setQuery(e.target.value)} autoFocus 
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results.length > 0) {
                e.preventDefault();
                if (!selected.find(s => s.id === results[0].id)) {
                  toggle(results[0]);
                }
                setQuery('');
              }
            }}
          />
          <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto' }}>
            {results.map(u => {
              const isSel = selected.find(x => x.id === u.id);
              return (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', cursor: 'pointer' }}
                  onClick={() => toggle(u)}>
                  <Avatar user={u} size={38} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: '#667781' }}>{u.phone}</div>
                  </div>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isSel ? 'var(--primary)' : '#ccc'}`, background: isSel ? 'var(--primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12 }}>
                    {isSel ? '✓' : ''}
                  </div>
                </div>
              );
            })}
            {query.length >= 2 && results.length === 0 && (
              <p style={{ color: '#667781', fontSize: 13, textAlign: 'center', padding: 16 }}>No new people found</p>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={adding || selected.length === 0}>
            {adding ? '⏳ Adding...' : `Add (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
